import urlBuilder from 'garaje/utils/url-builder';

export interface IncidentSummary {
  id: string;
  status: 'active' | 'ended';
  startedAt: string;
  endedAt: string | null;
}

export interface IncidentAnnouncementData {
  id: string;
  title: string;
  message: string;
  createdAt: string;
  locationName: string | null;
  sentEmployeesCount: number;
  sentVisitorsCount: number;
  safeCount: number;
  creatorUserId: string | null;
}

export interface IncidentUserData {
  id: string;
  name: string | null;
  profilePictureUrl: string | null;
}

export interface IncidentData {
  id: string;
  status: 'active' | 'ended';
  startedAt: string;
  endedAt: string | null;
  announcementIds: string[];
}

export interface IncidentsPageResult {
  incidents: IncidentData[];
  announcements: Map<string, IncidentAnnouncementData>;
  users: Map<string, IncidentUserData>;
  totalCount: number;
}

export function emptyIncidentsResult(): IncidentsPageResult {
  return { incidents: [], announcements: new Map(), users: new Map(), totalCount: 0 };
}

/* eslint-disable @typescript-eslint/no-explicit-any */
type JsonApiResource = {
  type: string;
  id: string;
  attributes?: Record<string, any>;
  relationships?: Record<string, any>;
};
/* eslint-enable @typescript-eslint/no-explicit-any */

/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-argument, ember/use-ember-get-and-set */
function parseIncidentsResponse(json: {
  data: JsonApiResource[];
  included?: JsonApiResource[];
  meta?: { record_count?: number };
}): IncidentsPageResult {
  const usersMap = new Map<string, IncidentUserData>();
  const announcementsMap = new Map<string, IncidentAnnouncementData>();

  for (const resource of json.included ?? []) {
    if (resource.type === 'users') {
      usersMap.set(resource.id, {
        id: resource.id,
        name: resource.attributes?.['name'] ?? null,
        profilePictureUrl: resource.attributes?.['profile_picture_url'] ?? null,
      });
    } else if (resource.type === 'announcements') {
      const stats = resource.attributes?.['statistics'];
      const actionCounts: { action: string; count: number }[] = stats?.['action_counts'] ?? [];
      const safeEntry = actionCounts.find((a) => a.action === 'mark_as_safe');
      const creatorRef = resource.relationships?.['creator_employee']?.data;

      announcementsMap.set(resource.id, {
        id: resource.id,
        title: resource.attributes?.['title'] ?? '',
        message: resource.attributes?.['message'] ?? '',
        createdAt: resource.attributes?.['created_at'] ?? '',
        locationName: resource.attributes?.['location_name'] ?? null,
        sentEmployeesCount: stats?.['sent_count']?.['employees_count'] ?? 0,
        sentVisitorsCount: stats?.['sent_count']?.['visitors_count'] ?? 0,
        safeCount: safeEntry?.count ?? 0,
        creatorUserId: creatorRef?.id ?? null,
      });
    }
  }

  const incidents: IncidentData[] = (json.data ?? []).map((resource) => {
    const announcementIds: string[] = (resource.relationships?.['announcements']?.data ?? []).map(
      (ref: { id: string }) => ref.id,
    );
    const rawStatus = resource.attributes?.['status'];
    const status: 'active' | 'ended' = rawStatus === 'active' ? 'active' : 'ended';
    return {
      id: resource.id,
      status,
      startedAt: resource.attributes?.['started_at'] ?? '',
      endedAt: resource.attributes?.['ended_at'] ?? null,
      announcementIds,
    };
  });

  return {
    incidents,
    announcements: announcementsMap,
    users: usersMap,
    totalCount: json.meta?.['record_count'] ?? 0,
  };
}
/* eslint-enable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-argument, ember/use-ember-get-and-set */

export async function fetchIncidentsPage(
  locationId: string,
  startDate: Date,
  endDate: Date,
  pageSize = 25,
  pageNumber = 1,
): Promise<IncidentsPageResult> {
  const url = urlBuilder.communication.incidentsUrl(locationId, startDate, endDate, pageSize, pageNumber);

  const response = await fetch(url, {
    credentials: 'same-origin',
    headers: { Accept: 'application/vnd.api+json' },
  });
  if (!response.ok) {
    throw new Error(`fetchIncidentsPage failed: ${response.status} ${response.statusText}`);
  }

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const json = await response.json();
  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
  return parseIncidentsResponse(json);
}

export async function fetchIncidentsByAnnouncementId(
  locationId: string,
  startDate: Date,
  endDate: Date,
): Promise<Map<string, IncidentSummary>> {
  try {
    const url = urlBuilder.communication.incidentsUrl(locationId, startDate, endDate, 100, 1);

    const response = await fetch(url, {
      credentials: 'same-origin',
      headers: { Accept: 'application/vnd.api+json' },
    });
    if (!response.ok) return new Map();

    const json = (await response.json()) as {
      data: {
        id: string;
        attributes: { status: 'active' | 'ended'; started_at: string; ended_at: string | null };
        relationships: { announcements: { data: { id: string }[] } };
      }[];
    };

    const map = new Map<string, IncidentSummary>();
    for (const incident of json.data ?? []) {
      const summary: IncidentSummary = {
        id: incident.id,
        status: incident.attributes.status,
        startedAt: incident.attributes.started_at,
        endedAt: incident.attributes.ended_at ?? null,
      };
      for (const ann of incident.relationships?.announcements?.data ?? []) {
        map.set(String(ann.id), summary); // eslint-disable-line ember/use-ember-get-and-set
      }
    }
    return map;
  } catch {
    return new Map();
  }
}
